// PE-30: the SLOT GROUP (superset/circuit) actions, split out of
// programEditActions.ts at its line cap. moveSlot and moveSlots joined them
// with #336: they have to know about groups to keep them whole; so did
// rename, trim and the join's set-count question (3b). Pure
// `(draft, …) => Draft` actions, re-exported through the barrel.
import { updateSlot } from "./programEditActions.js";
import { alignGroupSlots, groupMemberIdxs, setCountAt } from "./programEditRoundSets.js";
import type { Draft, SlotDraft, SlotGroupDraft } from "./programEdit.js";

/** The run of slots that moves as one: `idx` and the neighbours that share its
 *  group id, or `idx` alone. Inclusive bounds. Only ADJACENT members count, so
 *  a group already split in the data is never gathered behind the author.
 *  Also what the phone editor's selection takes when one member is tapped. */
export const groupRunAt = (slots: readonly SlotDraft[], idx: number): [number, number] => {
  const id = slots[idx]?.group?.id;
  let a = idx;
  let b = idx;
  if (!id) return [a, b];
  while (a > 0 && slots[a - 1]!.group?.id === id) a -= 1;
  while (b < slots.length - 1 && slots[b + 1]!.group?.id === id) b += 1;
  return [a, b];
};

/** Move a run of exercises one step up or down in its day (#336): the slots
 *  with these ids, which must sit next to each other. The run widens to any
 *  superset it cuts into, and a step past a superset jumps all of it: no move
 *  can split a group or land inside one. Ids that are not one run, none found,
 *  or the edge: the draft, unchanged (the editor disables the arrow on that). */
export const moveSlots = (
  draft: Draft,
  sIdx: number,
  slotIds: readonly string[],
  delta: -1 | 1,
): Draft => {
  const slots = draft.sessions[sIdx]?.slots;
  if (!slots) return draft;
  const wanted = new Set(slotIds);
  const idxs = slots.flatMap((s, i) => (wanted.has(s.id) ? [i] : []));
  if (idxs.length === 0) return draft;
  const lo = idxs[0]!;
  const hi = idxs[idxs.length - 1]!;
  if (hi - lo + 1 !== idxs.length) return draft;
  return moveRun(draft, sIdx, slots, lo, hi, delta);
};

/** Move an exercise one step up or down in its day (#336), with its superset:
 *  moveSlots for a run of one. */
export const moveSlot = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  delta: -1 | 1,
): Draft => {
  const slots = draft.sessions[sIdx]?.slots;
  if (!slots?.[slotIdx]) return draft;
  return moveRun(draft, sIdx, slots, slotIdx, slotIdx, delta);
};

const moveRun = (
  draft: Draft,
  sIdx: number,
  slots: SlotDraft[],
  lo: number,
  hi: number,
  delta: -1 | 1,
): Draft => {
  const a = groupRunAt(slots, lo)[0];
  const b = groupRunAt(slots, hi)[1];
  const neighbour = delta < 0 ? a - 1 : b + 1;
  if (neighbour < 0 || neighbour >= slots.length) return draft;
  const [c, d] = groupRunAt(slots, neighbour);
  const [from, to] = delta < 0 ? [c, b] : [a, d];
  const first = delta < 0 ? slots.slice(a, b + 1) : slots.slice(c, d + 1);
  const second = delta < 0 ? slots.slice(c, d + 1) : slots.slice(a, b + 1);
  const next = [...slots.slice(0, from), ...first, ...second, ...slots.slice(to + 1)];
  return {
    ...draft,
    sessions: draft.sessions.map((s, i) => (i === sIdx ? { ...s, slots: next } : s)),
  };
};

export const setSlotGroup = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
  group: SlotGroupDraft | null,
): Draft => updateSlot(draft, sIdx, slotIdx, { group });

/** #336: the superset's name on every member of `groupId` in day `sIdx`. An
 *  empty or blank name clears it, and the client shows its own default
 *  («Superset N») again. Unknown group: the draft, unchanged. */
export const renameGroup = (
  draft: Draft,
  sIdx: number,
  groupId: string,
  name: string,
): Draft => {
  const session = draft.sessions[sIdx];
  if (!session?.slots.some((s) => s.group?.id === groupId)) return draft;
  const clean = name.trim();
  const slots = session.slots.map((s) => {
    if (s.group?.id !== groupId) return s;
    const group: SlotGroupDraft = { ...s.group };
    if (clean) group.name = clean;
    else delete group.name;
    return { ...s, group };
  });
  return {
    ...draft,
    sessions: draft.sessions.map((x, i) => (i === sIdx ? { ...x, slots } : x)),
  };
};

/** The day's slots with `groupId` evened out round by round (Q7, #336 S1):
 *  alignGroupSlots, the one rule the save shares (buildPayload). */
const alignGroup = (draft: Draft, sIdx: number, groupId: string, mode: "pad" | "trim"): Draft => {
  const session = draft.sessions[sIdx];
  if (!session) return draft;
  const slots = alignGroupSlots(session.slots, groupId, mode);
  if (slots === session.slots) return draft;
  return {
    ...draft,
    sessions: draft.sessions.map((x, i) => (i === sIdx ? { ...x, slots } : x)),
  };
};

/** PE-30: pad every member of `groupId` up to the group's longest set count,
 *  in each round. A superset renders as interleaved rounds, so members with
 *  different counts leave orphaned rounds; `buildPayload` forces the same
 *  symmetry on save through the same alignGroupSlots, so the editor shows
 *  what is stored. A new set repeats the member's own last one
 *  (seedNextSetRow, no note).
 *
 *  ⛔ Never trims: dropping a prescribed row would lose volume, same reason
 *  the save path only ever pads. ⚠️ Deliberately NOT the logger's SS-3 rule
 *  (pad with EMPTY rows, loggerGroupReducer 'align_group_sets'): a live
 *  session's blank row is one the athlete fills as they go, while a program's
 *  blank row would be a prescription nobody wrote. The program's own
 *  convention wins on the program's own draft. */
export const alignGroupSetRows = (
  draft: Draft,
  sIdx: number,
  groupId: string,
): Draft => alignGroup(draft, sIdx, groupId, "pad");

/** #336: the other answer to a join with uneven set counts: every member of
 *  `groupId` cut down to the group's SHORTEST count in each round, from the
 *  end. Asked for explicitly (the editor's «Trim to M»), never applied on its
 *  own: it drops prescribed sets, which alignGroupSetRows above refuses to do. */
export const trimGroupSetRows = (
  draft: Draft,
  sIdx: number,
  groupId: string,
): Draft => alignGroup(draft, sIdx, groupId, "trim");

/** The set counts a join brings together, or null when there is nothing to
 *  ask: no partner, or every count already equal. The one owner of the rule
 *  for the logger's join (useJoinExistingGroup) and the program editor's. */
export const groupJoinCounts = (
  joining: number,
  others: readonly number[],
): { shortest: number; longest: number } | null =>
  others.length === 0 || others.every((c) => c === joining)
    ? null
    : { shortest: Math.min(joining, ...others), longest: Math.max(joining, ...others) };

/** groupJoinCounts for slot `slotIdx` joining the next exercise of the day
 *  (and the rest of its superset, if it has one), asked round by round (#336
 *  S1) as pad and trim work. `sameInEveryRound`: every round pads to
 *  `longest` and trims to `shortest`, so the answer can name them; otherwise
 *  they are the extremes over the rounds that differ. No next, or no round
 *  differs: null. */
export const nextSlotJoinCounts = (
  draft: Draft,
  sIdx: number,
  slotIdx: number,
): { shortest: number; longest: number; sameInEveryRound: boolean } | null => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const me = slots[slotIdx];
  const next = slots[slotIdx + 1];
  if (!me || !next) return null;
  const gid = next.group?.id;
  const all = [me, ...(gid ? groupMemberIdxs(slots, gid).map((i) => slots[i]!) : [next])];
  const rounds = Math.max(...all.map((s) => s.inactiveWeeks.length));
  const pads = new Set<number>();
  const trims = new Set<number>();
  let span: { shortest: number; longest: number } | null = null;
  for (let w = 0; w < rounds; w += 1) {
    const counts = all.filter((s) => !s.inactiveWeeks[w]).map((s) => setCountAt(s, w));
    if (counts.length === 0) continue;
    pads.add(Math.max(...counts));
    trims.add(Math.max(1, Math.min(...counts)));
    const here = groupJoinCounts(counts[0]!, counts.slice(1));
    if (!here) continue;
    span = span
      ? { shortest: Math.min(span.shortest, here.shortest), longest: Math.max(span.longest, here.longest) }
      : here;
  }
  return span ? { ...span, sameInEveryRound: pads.size === 1 && trims.size === 1 } : null;
};
